import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, combineLatest, concat, Observable, of, Subject, Subscription } from 'rxjs';
import { WorklistService } from '../worklist.service';
import {
    GetReferralDetails,
    PatchOrderRequest,
    PatchProcedureRequest,
    ReferringDoctorDTO,
    ScanDto,
    StatusType,
    StrokeProtocol,
    UrgencyType,
    WorklistItemDataSourceType,
    WorklistItemDetailsDto,
    WorklistItemDto
} from '../../api-module/control-centre-api-client';
import { map, mergeMap, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { AbstractControl, FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Constants } from '../../models/constants';
import { SnackbarService } from '../../services/snackbar.sevice';
import { Clipboard } from '@angular/cdk/clipboard';
import { EditableDirective } from '../../app-common/editable/editable.directive';
import { MessageBoxButtons, MessageBoxIcon, MessageBoxResult } from '../../messagebox/messagebox.model';
import { MessageBoxService } from '../../messagebox/services/messagebox.service';
import { AutocompleteService } from '../../order/services/autocomplete.service';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import { FeaturesService } from 'src/app/services/features.service';
import { ReferralsService } from 'src/app/referrals/referrals.service';
import { ReferralsUtility } from 'src/app/referrals/referrals.utility';
import { ViewReferralComponent } from 'src/app/referrals/view-referral/view-referral.component';
import { MatDialog } from '@angular/material/dialog';
import { OrgSiteSelectionService } from 'src/app/shared/org-site-selection/org-site-selection.service';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { DropdownOption } from '@app/models/dropdown-option';
import { OrderLockStatus } from './details.models';

@Component({
    selector: 'app-worklist-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DetailsComponent implements OnChanges, OnDestroy {
    @Input()
    worklistItem: WorklistItemDto;

    @Output()
    closePane = new EventEmitter<void>();

    @Output()
    updateItemUrgency = new EventEmitter<WorklistItemDetailsDto>();

    public urgencyType = UrgencyType;
    public strokeProtocol = StrokeProtocol;

    urgencyDropdownOptions = new BehaviorSubject<DropdownOption<UrgencyType | StrokeProtocol>[]>([]);
    urgencyDropdownPlaceholder = new BehaviorSubject<string>('Please select');

    worklistItem$: Observable<WorklistItemDetailsDto>;
    worklistItemDetail?: WorklistItemDetailsDto;
    linkedReferralDetails$ = new BehaviorSubject<GetReferralDetails>(null);

    editableSubject = new BehaviorSubject<boolean>(false);
    fileUploadableSubject = new BehaviorSubject<boolean>(false);
    routineSubject = new BehaviorSubject<boolean>(false);

    scanDataSource = new MatTableDataSource<ScanDto>();
    scanColumns: string[] = ['modality', 'description', 'imageCount', 'accessionNumber'];

    urgencyOptions = [UrgencyType.Trauma, UrgencyType.Stroke];
    @ViewChild('urgencyDropdown') urgencyDropdown: MatSelect;

    urgencyDetailsText$: Observable<string>;
    isUrgencyDropdownSaved = false;
    isUrgencyDropdownEnabled$: Observable<boolean>;
    private readonly isUrgencyDetailsValid = new BehaviorSubject<boolean>(false);
    isUrgencyDetailsValid$ = this.isUrgencyDetailsValid.asObservable();

    isFieldSaving = new BehaviorSubject<boolean>(false);

    filteredReferringDoctors$: Observable<{ text: string; value: string }[]>;
    filteredReferringDoctorPhoneNo$: Observable<{ text: string; value: string }[]>;
    referringDoctorOptions$: Observable<{ text: string; value: string }>;
    referringDoctorPhoneOptions$: Observable<{ text: string; value: string }>;

    suggestedPatientLocation$: Observable<{ text: string; value: string }[]>;

    detailsForm: UntypedFormGroup;
    fieldsInitialValue: Map<string, string> = new Map();

    lockStatus: OrderLockStatus = { Icon: null, Tooltip: null };

    referringDoctorFormControl = new FormControl('', {
        validators: [Validators.required, Validators.maxLength(64), Validators.pattern(Constants.NamePattern)],
        updateOn: 'blur'
    });
    referringDoctorPhoneFormControl = new FormControl('', {
        validators: [Validators.required, Validators.maxLength(32), Validators.pattern(Constants.FullPhonePattern)],
        updateOn: 'blur'
    });
    radiographerNameFormControl = new FormControl('', {
        validators: [Validators.maxLength(64), Validators.pattern(Constants.NamePattern)],
        updateOn: 'blur'
    });
    patientLocationFormControl = new FormControl('', {
        validators: [Validators.required, Validators.maxLength(64), Validators.pattern(Constants.LocationNamePattern)],
        updateOn: 'blur'
    });
    scanTableImageCountsFormControls: FormControl[] = [];
    notesToRadiographerFormControl = new FormControl('', { updateOn: 'blur' });

    private readonly updateTrigger = new BehaviorSubject<WorklistItemDto | WorklistItemDetailsDto>(null);
    private readonly resetFormTrigger = new Subject();
    private readonly formFields$: Observable<WorklistItemDetailsDto>;
    private readonly subscriptions = new Subscription();

    static readonly isWorklistItemRoutine = (item: WorklistItemDto) => item?.urgency === UrgencyType.Routine;

    static readonly isWorklistItemEditable = (item: WorklistItemDetailsDto): boolean =>
        item &&
        item.dataSource === WorklistItemDataSourceType.Order &&
        !item.hasAssignedTo &&
        item.status !== StatusType.Reported &&
        item.status !== StatusType.Cancelled;

    static isWorklistItemAllowedToUpload(item: WorklistItemDetailsDto): boolean {
        if (DetailsComponent.isWorklistItemEditable(item)) {
            return true;
        }
        if (item && item.dataSource === WorklistItemDataSourceType.Order) {
            if (item.hasAssignedTo && item.onHold && item.status !== StatusType.Reported && item.status !== StatusType.Cancelled) {
                return true;
            }
            if (item.status === StatusType.Reported && item.hasActiveAddenda) {
                return true;
            }
        }
        return false;
    }

    constructor(
        private readonly worklistService: WorklistService,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly snackbarService: SnackbarService,
        private readonly clipboard: Clipboard,
        private readonly messageBoxService: MessageBoxService,
        private readonly authenticationService: AuthenticationService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly featuresService: FeaturesService,
        private readonly referralsService: ReferralsService,
        private readonly dialog: MatDialog,
        private readonly autocompleteService: AutocompleteService
    ) {
        this.worklistItem$ = combineLatest([this.updateTrigger]).pipe(
            map(([item]) => {
                if (item instanceof WorklistItemDetailsDto) {
                    // Receiving from form update. Update the observable and trigger a remote update too.
                    return concat(of(item), this.worklistService.get(item.id, item.dataSource));
                } else if (item?.id) {
                    // Receiving from @Input change
                    return this.worklistService.get(item.id, item.dataSource);
                } else {
                    // No data, show loading
                    return of(null);
                }
            }),
            mergeMap(item => item),
            shareReplay()
        );

        this.subscriptions.add(
            this.worklistItem$.subscribe(item => {
                if (item) {
                    this.resetForm();
                    this.initFormFieldsDefaultValue(item);
                }
                this.worklistItemDetail = item;
            })
        );

        this.subscriptions.add(
            this.worklistItem$
                .pipe(map(DetailsComponent.isWorklistItemEditable))
                .subscribe(isEditable => this.editableSubject.next(isEditable))
        );

        this.subscriptions.add(
            this.worklistItem$
                .pipe(map(DetailsComponent.isWorklistItemAllowedToUpload))
                .subscribe(canUploadFile => this.fileUploadableSubject.next(canUploadFile))
        );

        this.subscriptions.add(this.worklistItem$.subscribe(item => (this.lockStatus = OrderLockStatus.fromWorklistItem(item))));

        this.subscriptions.add(
            this.worklistItem$.pipe(map(DetailsComponent.isWorklistItemRoutine)).subscribe(isRoutine => this.routineSubject.next(isRoutine))
        );
        this.subscriptions.add(
            this.editableSubject.subscribe(isEditable => {
                this.setControlIsEditable(this.referringDoctorFormControl, isEditable);
                this.setControlIsEditable(this.referringDoctorPhoneFormControl, isEditable);
                this.setControlIsEditable(this.radiographerNameFormControl, isEditable);
                this.setControlIsEditable(this.patientLocationFormControl, isEditable);
                this.setControlIsEditable(this.notesToRadiographerFormControl, isEditable);

                this.scanTableImageCountsFormControls.forEach(control => this.setControlIsEditable(control, isEditable));
            })
        );

        this.formFields$ = combineLatest([this.resetFormTrigger, this.worklistItem$]).pipe(switchMap(([_, item]) => of(item)));

        const referringDoctors$: Observable<ReferringDoctorDTO[]> = this.worklistItem$.pipe(
            switchMap(item => {
                if (!item?.siteId) {
                    return of([]);
                }
                return this.autocompleteService.getReferringDoctors(item.siteId);
            }),
            shareReplay()
        );

        this.subscriptions.add(
            this.worklistItem$
                .pipe(
                    map(worklistItem => {
                        if (worklistItem?.accessionNumber) {
                            return this.referralsService.getReferralLinkingDetails(worklistItem.accessionNumber);
                        } else {
                            return of(null);
                        }
                    }),
                    mergeMap(item => item)
                )
                .subscribe(result => {
                    this.linkedReferralDetails$.next(result?.linkedReferral);
                })
        );

        const linkedReferralDoctor$ = this.linkedReferralDetails$.pipe(
            map(linkedReferralDetails => {
                if (linkedReferralDetails?.referrerName) {
                    return new ReferringDoctorDTO({
                        name: linkedReferralDetails.referrerName,
                        phone: linkedReferralDetails.referrerPhone
                    });
                } else {
                    return null;
                }
            })
        );

        this.filteredReferringDoctors$ = combineLatest([referringDoctors$, linkedReferralDoctor$]).pipe(
            switchMap(([referringDoctors, linkedReferralDoctor]) =>
                this.referringDoctorFormControl.valueChanges.pipe(
                    startWith(''),
                    map(doctorNameInput => {
                        const filterValue = doctorNameInput?.toLowerCase() ?? '';
                        let result: ReferringDoctorDTO[];

                        if (referringDoctors && filterValue && filterValue.length >= 3) {
                            result = referringDoctors
                                .filter(option => option.name.toLowerCase().includes(filterValue)) // Filter by keyword
                                .sort((a, b) => b.expiry - a.expiry) // sort by expiry latest first.
                                .reduce((arr, v) => {
                                    // Reduce duplicate
                                    if (arr.map(dto => dto.name).indexOf(v.name) < 0) {
                                        arr.push(v);
                                    }
                                    return arr;
                                }, [])
                                .sort((a, b) => a.name - b.name); // sort alphabetically
                        } else {
                            result = [];
                        }

                        // Always put the referring doctor in the referral first, if it exists
                        const outcome = linkedReferralDoctor ? [linkedReferralDoctor, ...result.filter(r => r.name?.toLowerCase() !== linkedReferralDoctor.name?.toLowerCase())] : result;

                        return outcome.map(d => ({ text: d.name, value: d.name }));
                    })
                )
            )
        );

        this.filteredReferringDoctorPhoneNo$ = combineLatest([referringDoctors$, linkedReferralDoctor$]).pipe(
            map(([referringDoctors, linkedReferralDoctor]) => {
                let selectedDoctorName = this.referringDoctorFormControl.value;
                if (!selectedDoctorName) {
                    return [];
                }

                selectedDoctorName = selectedDoctorName.toLowerCase();
                if (selectedDoctorName == linkedReferralDoctor?.name?.toLowerCase()) {
                    return [linkedReferralDoctor];
                } else {
                    return referringDoctors
                        .filter(d => d.name.toLowerCase() === selectedDoctorName)
                        .reduce((arr, v) => {
                            // Reduce duplicate
                            if (
                                arr
                                    .map(dto => this.combinePhoneNumber(dto.countryCode, dto.phone, dto.extension))
                                    .indexOf(this.combinePhoneNumber(v.countryCode, v.phone, v.extension)) < 0
                            ) {
                                arr.push(v);
                            }
                            return arr;
                        }, [])
                        .sort((a, b) => b.expiry - a.expiry); // sort by expiry latest first
                }
            }),
            switchMap(referringDoctors =>
                this.referringDoctorPhoneFormControl.valueChanges.pipe(
                    startWith(''),
                    map(phoneInput => {
                        let result: ReferringDoctorDTO[];

                        if (referringDoctors) {
                            result = referringDoctors.filter(option => option.phone.includes(phoneInput)); // Filter by keyword
                        } else {
                            result = [];
                        }

                        return result.map(p => ({ text: p.phone, value: p.phone }));
                    })
                )
            )
        );

        this.suggestedPatientLocation$ = this.linkedReferralDetails$.pipe(
            map(details => {
                const suggestedLocation = details?.patient?.location;
                return suggestedLocation ? [{ text: suggestedLocation, value: suggestedLocation }] : [];
            })
        );

        this.subscriptions.add(
            this.formFields$.subscribe(item => {
                if (!this.detailsForm) {
                    this.detailsForm = this.formBuilder.group({
                        urgencyDropdown: [item?.urgency, [Validators.required]]
                    });
                }

                if (item) {
                    // to reduce the unnecessary DOM refresh for empty worklistItem
                    this.resetUrgencyDropdownSelection(item);
                    this.assignScanDataSource(item.scans);
                }
            })
        );

        this.isUrgencyDropdownEnabled$ = combineLatest([this.editableSubject, this.isFieldSaving]).pipe(
            map(([isOrderEditable, isOrderSaving]) => isOrderEditable && !isOrderSaving)
        );

        const strokeQueryTypesEnabled = this.areStrokeQueryTypesEnabled().pipe(
            map(strokeQueryTypeEnabledForCountry => strokeQueryTypeEnabledForCountry)
        );

        this.urgencyDetailsText$ = combineLatest([this.worklistItem$, strokeQueryTypesEnabled]).pipe(
            map(([item, enabled]) => this.displayUrgencyDetail(item, enabled))
        );

        const updateUrgencyDropdownOptionTriggers = combineLatest([this.worklistItem$, strokeQueryTypesEnabled]);
        this.subscriptions.add(
            updateUrgencyDropdownOptionTriggers.subscribe(([worklistItem, strokeQueryTypesEnabled]) => {
                // Make a copy of the top level elements so we do not edit the original.
                const urgencyDropdownOptions: DropdownOption<UrgencyType | StrokeProtocol>[] = Constants.UrgencyDropdownOptions.map(
                    option => ({ ...option })
                );
                if (strokeQueryTypesEnabled) {
                    const strokeOption = urgencyDropdownOptions.find(option => option.value === UrgencyType.Stroke);
                    strokeOption.subValues = Constants.StrokeDropdownOptions;
                }

                this.urgencyDropdownOptions.next(urgencyDropdownOptions);

                // We need to handle mismatches between what the available selections are and what is set.
                // E.g. if an OC sets an order to Stroke - Thrombolysis and the client cannot select Thrombolysis.
                let urgencyPlaceholderText = 'Please select';
                if (worklistItem) {
                    if (strokeQueryTypesEnabled && worklistItem.strokeProtocolType === StrokeProtocol.Unspecified) {
                        urgencyPlaceholderText = 'Stroke';
                    } else if (!strokeQueryTypesEnabled) {
                        const matchingStrokeQuerySubType = Constants.StrokeDropdownOptions.find(
                            option => option.value == worklistItem.strokeProtocolType
                        );
                        if (matchingStrokeQuerySubType) {
                            urgencyPlaceholderText = `Stroke - ${matchingStrokeQuerySubType.label}`;
                        }
                    }
                }
                this.urgencyDropdownPlaceholder.next(urgencyPlaceholderText);
            })
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.worklistItem?.firstChange || changes.worklistItem?.previousValue?.id !== changes.worklistItem?.currentValue?.id) {
            this.updateTrigger.next(null);
            this.clearUrgencyDropdownSavingStatus();
            this.fieldsInitialValue.clear();
        }

        if (changes.worklistItem) {
            this.updateTrigger.next(changes.worklistItem.currentValue);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    async close() {
        if (!this.detailsForm?.dirty) {
            const result = await this.checkIfRequiredToUpdateUrgency();
            if (!result) {
                this.closePane.emit();
            }
        }
    }

    private isAllControlsPristine(formElement: AbstractControl) {
        if (formElement instanceof UntypedFormArray) {
            const ctrlArr = formElement.controls as Array<AbstractControl>;
            return ctrlArr.every(ctrl => this.isAllControlsPristine(ctrl));
        } else if (formElement instanceof UntypedFormGroup) {
            const ctrlArr = Object.values(formElement.controls);
            return ctrlArr.every(ctrl => this.isAllControlsPristine(ctrl));
        } else {
            return formElement.pristine;
        }
    }

    public combinePhoneNumber(countryCode: string, phoneNumber: string, extension: string): string {
        let fullPhoneNumber = countryCode ? countryCode + phoneNumber : phoneNumber;
        if (extension) {
            fullPhoneNumber += Constants.PhoneExtensionBrief + extension;
        }
        return fullPhoneNumber.replace(/ /g, '');
    }

    async handleInputUpdate(control: FormControl, serviceCall: () => Promise<void>, initialValueKey: string) {
        if (control.dirty && control.invalid) {
            const confirmation = await this.validationErrorMessage();
            if (confirmation === MessageBoxResult.No) {
                const inputDefaultVal = this.fieldsInitialValue.get(initialValueKey);
                control.setValue(inputDefaultVal);
                control.markAsPristine();
                return;
            }
        }

        if (!control.dirty || control.invalid || control.value == this.fieldsInitialValue.get(initialValueKey)) {
            return;
        }

        this.isFieldSaving.next(true);
        try {
            control.disable();
            await serviceCall();
            this.snackbarService.openSnackBar('Updated', SnackbarService.SNACK_BAR_CLASS_SUCCESS);

            this.fieldsInitialValue.set(initialValueKey, control.value);
            await this.checkIfRequiredToUpdateUrgency();
        } catch (e) {
            this.snackbarService.openSnackBar(Constants.UnableToContactServerMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
        } finally {
            this.isFieldSaving.next(false);
            control.markAsPristine();
            control.enable();
        }
    }

    async handleOrderInputUpdate(fieldname: string, control: FormControl, extraFields = {}) {
        const payload = extraFields ?? {};
        payload[fieldname] = control.value;

        const request = new PatchOrderRequest();
        for (const property in payload) {
            request[property] = payload[property];
        }

        const serviceCall = () => this.worklistService.patchOrder(this.worklistItemDetail.id, request);
        await this.handleInputUpdate(control, serviceCall, fieldname);
    }

    async handleProcedureInputUpdate(fieldname: string, control: FormControl, procedureId: number) {
        const request = new PatchProcedureRequest();
        request[fieldname] = control.value;

        const serviceCall = async () => {
            if (!this.worklistItemDetail.radiographerName) {
                request.radiographerName = this.authenticationService.getCurrentUser()?.username;
            }
            await this.worklistService.patchProcedure(this.worklistItemDetail.id, procedureId, request);
        };
        await this.handleInputUpdate(control, serviceCall, `${fieldname}_${procedureId}`);
    }

    async updateRadiographerNotes() {
        if (this.isFieldSaving.value) {
            return;
        }

        const payload = {};
        if (!this.worklistItemDetail.radiographerName) {
            payload['radiographerName'] = this.authenticationService.getCurrentUser()?.username;
        }

        const value = this.notesToRadiographerFormControl.value.replace(/\r/g, '').replace(/\n/g, Constants.SeperatorInNotes);
        this.notesToRadiographerFormControl.setValue(value);

        await this.handleOrderInputUpdate('radiographerNotes', this.notesToRadiographerFormControl, payload);
    }

    async confirmChange() {
        const title = 'Please Confirm';
        const message = 'Do you want to save your changes?';
        const information = 'You have unsaved changes.';
        return await this.messageBoxService
            .showMessageBox({
                title,
                message,
                information,
                icon: MessageBoxIcon.Confirmation,
                buttons: MessageBoxButtons.YesNo
            })
            .toPromise();
    }

    async validationErrorMessage() {
        const title = 'There is an error with your input';
        const message = `The information you've entered does not meet our criteria, would you like to change this now?`;
        return await this.messageBoxService
            .showMessageBox({
                title,
                message,
                information: null,
                icon: MessageBoxIcon.Information,
                buttons: MessageBoxButtons.YesNo
            })
            .toPromise();
    }

    onUrgencyDropdownChanged(matSelectChange: MatSelectChange, item: WorklistItemDetailsDto) {
        const selectedOption: UrgencyType | StrokeProtocol = matSelectChange.value;
        let urgency: UrgencyType;
        let traumaProtocol = false;
        let strokeProtocolType: StrokeProtocol = StrokeProtocol.None;
        switch (selectedOption) {
            case UrgencyType.Urgent:
                urgency = UrgencyType.Urgent;
                break;
            case UrgencyType.Trauma:
                urgency = UrgencyType.Trauma;
                traumaProtocol = true;
                break;
            case UrgencyType.Stroke:
                urgency = UrgencyType.Stroke;
                strokeProtocolType = StrokeProtocol.Unspecified;
                break;
            case StrokeProtocol.SimpleStroke:
            case StrokeProtocol.Thrombectomy:
            case StrokeProtocol.Thrombolysis:
                urgency = UrgencyType.Stroke;
                strokeProtocolType = selectedOption;
                break;

            case StrokeProtocol.None:
            case StrokeProtocol.Unspecified:
            case UrgencyType.Routine:
            case UrgencyType.TraumaAndStroke:
            default:
                console.error(`Unexpected dropdown selection: '${selectedOption}'`);
                return;
        }

        if (this.isFieldSaving.value) {
            return;
        }

        const payload = new PatchOrderRequest();
        payload.urgency = urgency;
        payload.traumaProtocol = traumaProtocol;
        payload.strokeProtocolType = strokeProtocolType;
        this.isFieldSaving.next(true);
        this.snackbarService.openSnackBar('Saving', SnackbarService.SNACK_BAR_CLASS_PROCESSING, null);
        this.worklistService
            .patchOrder(item.id, payload)
            .then(() => {
                item.urgency = urgency;
                item.traumaProtocol = traumaProtocol;
                item.strokeProtocolType = strokeProtocolType;
                this.updateTrigger.next(item);
                this.updateItemUrgency.emit(item);
                this.snackbarService.openSnackBar('Updated', SnackbarService.SNACK_BAR_CLASS_SUCCESS);
                this.formControls.urgencyDropdown.markAsPristine();
                this.formControls.urgencyDropdown.markAsUntouched();
                this.isUrgencyDropdownSaved = true;
            })
            .catch(_ => {
                this.snackbarService.openSnackBar(Constants.UnableToContactServerMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
                this.isUrgencyDropdownSaved = false;
            })
            .finally(() => {
                this.isFieldSaving.next(false);
            });
    }

    private async checkIfRequiredToUpdateUrgency() {
        if (this.editableSubject.value && !this.routineSubject.value && !this.formControls.urgencyDropdown.value) {
            const title = 'Please Select';
            const message = 'Please select an option from the Urgency Level dropdown.';
            return await this.messageBoxService
                .showMessageBox({
                    title,
                    message,
                    information: '',
                    icon: MessageBoxIcon.Confirmation,
                    buttons: MessageBoxButtons.Ok
                })
                .toPromise();
        }
        return Promise.resolve(null);
    }

    private assignScanDataSource(scans?: ScanDto[]) {
        if (!scans) {
            this.scanDataSource.data = [];
            return;
        }

        let updateSource = this.scanDataSource.data.length != scans.length;
        this.scanDataSource.data.every((scan, index) => {
            const newScan = scans.at(index);

            updateSource =
                scan.studyDescription != newScan?.studyDescription || scan.accessionNumber != newScan?.accessionNumber || updateSource;
            return !updateSource;
        });

        if (updateSource) {
            this.scanDataSource.data = scans;
        }
    }

    private resetUrgencyDropdownSelection(item: WorklistItemDetailsDto) {
        this.urgencyDropdown?.close();
        const urgencyDropdownControl = this.formControls.urgencyDropdown;
        if (item) {
            if (item.traumaProtocol) {
                urgencyDropdownControl.setValue(UrgencyType.Trauma);
            } else if (item.strokeProtocolType && item.strokeProtocolType !== StrokeProtocol.None) {
                if (item.strokeProtocolType !== StrokeProtocol.Unspecified) {
                    urgencyDropdownControl.setValue(item.strokeProtocolType);
                } else {
                    urgencyDropdownControl.setValue(UrgencyType.Stroke);
                }
            } else if (item.traumaProtocol === false && item.strokeProtocolType === StrokeProtocol.None) {
                urgencyDropdownControl.setValue(UrgencyType.Urgent);
            } else {
                urgencyDropdownControl.setValue(null);
            }
            urgencyDropdownControl.markAsPristine();
            urgencyDropdownControl.markAsTouched();
        }
    }

    private clearUrgencyDropdownSavingStatus() {
        this.isUrgencyDropdownSaved = false;
    }

    private areStrokeQueryTypesEnabled(): Observable<boolean> {
        return this.orgSiteSelectionService.getOrgSelected$().pipe(
            map(org => this.featuresService.isFeatureEnabled('strokeQueryType', org.countryCode)),
            shareReplay()
        );
    }

    handleInputAutocomplete(inputControl: FormControl, event: KeyboardEvent) {
        const inputElement = event.target as HTMLInputElement;
        inputControl.setValue(inputElement.value);
    }

    updateWorklistFromPrior(item: WorklistItemDetailsDto) {
        this.updateTrigger.next(item);
    }

    resetForm(editable: EditableDirective = null, formField: AbstractControl = null) {
        if (editable) {
            editable.close();
        }
        if (formField) {
            formField.markAsPristine();
        }

        this.resetFormTrigger.next(null);
    }

    get formControls() {
        return this.detailsForm.controls;
    }

    get shouldShowUrgencyDropdownError() {
        return (
            !this.isUrgencyDropdownSaved &&
            this.formControls.urgencyDropdown.invalid &&
            (this.formControls.urgencyDropdown.dirty || this.formControls.urgencyDropdown.touched)
        );
    }

    getStatusDescription(status: StatusType) {
        return Constants.StatusDescriptions.get(status);
    }

    private displayUrgencyDetail(item: WorklistItemDetailsDto, includeStrokeQueryType = false): string {
        if (!item) {
            return '';
        }
        if (item.urgency === UrgencyType.Routine) {
            return UrgencyType.Routine;
        } else {
            let detailText = '';
            const isStroke = item.strokeProtocolType && item.strokeProtocolType !== StrokeProtocol.None;
            if (item.urgency === UrgencyType.Urgent && !item.traumaProtocol && !isStroke) {
                return 'Urgent';
            }

            if (item.traumaProtocol) {
                detailText += 'Polytrauma';
            }

            if (isStroke) {
                if (item.traumaProtocol) {
                    detailText += ', ';
                }
                detailText += 'Stroke';
            }

            if (
                includeStrokeQueryType &&
                item.strokeProtocolType &&
                item.strokeProtocolType !== StrokeProtocol.None &&
                item.strokeProtocolType !== StrokeProtocol.Unspecified
            ) {
                detailText += ` - ${item.strokeProtocolType === StrokeProtocol.SimpleStroke ? 'Simple Stroke' : item.strokeProtocolType}`;
            }
            return detailText;
        }
    }

    copyReport(patientMrn: string, patientFirstName: string, patientLastName: string, studyDate: string, reportBody: string) {
        const firstName = patientFirstName || 'Unknown';
        const lastName = patientLastName || 'Unknown';

        const clipboardText = `Patient: ${patientMrn}
Name: ${firstName} ${lastName}
Study Date: ${studyDate}

${reportBody}`;
        this.clipboard.copy(clipboardText);
        this.snackbarService.openSnackBar('Copied to clipboard', SnackbarService.SNACK_BAR_CLASS_SUCCESS);
    }

    viewLinkedReferral(linkedReferral: GetReferralDetails) {
        const dialogConfig = ReferralsUtility.getViewReferralMatDialogConfig({ referralDetails: linkedReferral });

        this.dialog.open(ViewReferralComponent, dialogConfig);
    }

    private setControlIsEditable(formcontrol: FormControl, isEditable: boolean) {
        !isEditable ? formcontrol.disable() : formcontrol.enable();
    }

    private assignNewControlValue<T>(controlname: string, control: AbstractControl<T>, value: T) {
        const initialValue = this.fieldsInitialValue.get(controlname);
        // only assing a value if it's initial value has changed or no initial value exists
        if (!this.fieldsInitialValue.has(controlname) || initialValue != value) {
            control.setValue(value);
        }
    }

    private initFormFieldsDefaultValue(item: WorklistItemDetailsDto) {
        this.assignNewControlValue('referringDoctor', this.referringDoctorFormControl, item.referringDoctor);
        this.assignNewControlValue('referringDoctorPhoneNo', this.referringDoctorPhoneFormControl, item.referringDoctorPhone);
        this.assignNewControlValue('radiographerName', this.radiographerNameFormControl, item.radiographerName);
        this.assignNewControlValue('patientLocation', this.patientLocationFormControl, item.patientLocation);
        this.assignNewControlValue('radiographerNotes', this.notesToRadiographerFormControl, item.radiographerNotes);

        // cut off any extra controls if less scans
        if (item?.scans.length < this.scanTableImageCountsFormControls?.length) {
            this.scanTableImageCountsFormControls.splice(
                item.scans.length - 1,
                this.scanTableImageCountsFormControls.length - item.scans.length
            );
        }

        // only add a new formcontrol if the initial value has changed or more scans are available
        item?.scans.forEach((scan, index, _) => {
            const useOld =
                (scan?.expectedImageCount ?? null) !== null &&
                this.scanTableImageCountsFormControls[index] &&
                this.fieldsInitialValue.get('expectedImageCount_' + scan?.id) == scan?.expectedImageCount?.toString();
            const newImageCountControl = new FormControl(
                { value: scan?.expectedImageCount, disabled: this.editableSubject.value === false },
                [Validators.required, Validators.min(1), Validators.max(9999999)]
            );
            if (index >= this.scanTableImageCountsFormControls.length) {
                this.scanTableImageCountsFormControls.push(newImageCountControl);
            } else if (!useOld) {
                this.scanTableImageCountsFormControls[index] = newImageCountControl;
            }

            this.fieldsInitialValue.set('expectedImageCount_' + scan?.id, (scan?.expectedImageCount ?? 0).toString());
        });

        this.fieldsInitialValue.set('referringDoctor', item.referringDoctor);
        this.fieldsInitialValue.set('referringDoctorPhoneNo', item.referringDoctorPhone);
        this.fieldsInitialValue.set('radiographerName', item.radiographerName);
        this.fieldsInitialValue.set('patientLocation', item.patientLocation);
        this.fieldsInitialValue.set('radiographerNotes', item.radiographerNotes);
    }

    inputControlErrorMessage(label: string, formcontrol: FormControl, softerWordingFForRequired: boolean = false) {
        if (!formcontrol?.invalid) {
            return;
        }

        let errorMsg = '';
        if (formcontrol.hasError('required')) {
            if (softerWordingFForRequired) {
                errorMsg += label + ' is missing.';
            } else {
                errorMsg += label + ' is required.';
            }
        }
        if (formcontrol.hasError('maxlength')) {
            const lengthErr = formcontrol.getError('maxlength');
            errorMsg += label + ' must be no more than ' + lengthErr.requiredLength + ' characters long.';
        }
        if (formcontrol.hasError('pattern')) {
            errorMsg += 'Invalid characters in ' + label + '.';
        }
        if (formcontrol.hasError('min')) {
            const minErr = formcontrol.getError('min');
            errorMsg += `${label} cannot be smaller than ${minErr.min}.`;
        }
        if (formcontrol.hasError('max')) {
            const maxErr = formcontrol.getError('max');
            errorMsg += `${label} cannot be greater than ${maxErr.max}.`;
        }

        return errorMsg;
    }
}
